'use client';

import { c as _c } from "react/compiler-runtime";
import { type Node } from '@react-types/shared';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { type AriaTabListProps, type AriaTabPanelProps, type AriaTabProps, useTab, useTabList, useTabPanel } from 'react-aria';
import { Item, type TabListState, useTabListState } from 'react-stately';
import { BackToHomepageButton } from '@module/mdrt-org/shared/components/back-to-homepage-button/back-to-homepage-button';
import { useGlobalLoaderContext } from '@module/mdrt-org/shared/components/providers/global-loader-context';
import { type PatchAssessmentQuestions } from '@module/mdrt-org/shared/utils/data/patch-assessment-questions';
import { AssessmentQuestionsTopic } from '@module/mdrt-org/shared/utils/enums/assessment-questions-topic';
import { useAssessmentQuestionsMutation } from '@module/mdrt-org/shared/utils/hooks/use-assessment-questions-mutation';
import { useConfirmNavigation } from '@module/mdrt-org/shared/utils/hooks/use-confirm-navigation';
import { useMutationHandler } from '@module/mdrt-org/shared/utils/hooks/use-mutation-handler';
import { type SelfAssessment } from '@module/mdrt-org/shared/utils/types/self-assessment';
import { Alert } from '@shared/ui-components/alert/alert';
import { type PersonQuizPageContent } from './components/quiz-page-content-wrapper/get-person-quiz-page-content';
import { TabPanelSkeleton } from './components/quiz-page-skeleton-loader/quiz-page-skeleton-loader';
import { QuizTabItem } from './components/quiz-tabs-navigation/quiz-tab-item';
import { getInitialSelfAssessmentFormData } from './components/self-assessment-form/helpers';
import { SelfAssessmentForm } from './components/self-assessment-form/self-assessment-form';
import styles from './quiz-page-content-tabs.module.scss';
type TabData = {
  assessmentScore: number;
  currentTopicData: undefined | {
    questions: Array<{
      question: string;
      questionId: string;
      score: number;
    }>;
    topic: AssessmentQuestionsTopic;
    topicScore: number;
  };
  tabDescription: null | string | undefined;
  tabTitle: null | string | undefined;
  topic: AssessmentQuestionsTopic;
};
type TabItemProps = AriaTabProps & TabData;
const Tab = t0 => {
  const $ = _c(11);
  const {
    item,
    state,
    topicContainerRef
  } = t0;
  const {
    key,
    value
  } = item;
  const ref = useRef(null);
  let t1;
  if ($[0] !== key) {
    t1 = {
      key
    };
    $[0] = key;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    isSelected,
    tabProps
  } = useTab(t1, state, ref);
  if (value === null) {
    return null;
  }
  let t2;
  if ($[2] !== topicContainerRef) {
    t2 = () => {
      setTimeout(() => {
        ref.current?.scrollIntoView({
          behavior: "instant",
          block: "nearest",
          inline: "center"
        });
      }, 0);
      setTimeout(() => {
        topicContainerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest"
        });
      }, 0);
    };
    $[2] = topicContainerRef;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = value?.tabTitle;
  let t4;
  if ($[4] !== isSelected || $[5] !== t2 || $[6] !== t3 || $[7] !== tabProps || $[8] !== value.assessmentScore || $[9] !== value.key) {
    t4 = <QuizTabItem assessmentScore={value.assessmentScore} buttonProps={tabProps} isActive={isSelected} key={value.key} onClick={t2} ref={ref} scoreLabel={t3} />;
    $[4] = isSelected;
    $[5] = t2;
    $[6] = t3;
    $[7] = tabProps;
    $[8] = value.assessmentScore;
    $[9] = value.key;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
const TabPanel = t0 => {
  const $ = _c(8);
  let props;
  let state;
  if ($[0] !== t0) {
    ({
      state,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = state;
  } else {
    props = $[1];
    state = $[2];
  }
  const ref = useRef(null);
  const {
    tabPanelProps
  } = useTabPanel(props, state, ref);
  let t1;
  if ($[3] !== state.selectedItem) {
    t1 = state.selectedItem ? state.selectedItem.props.children : <TabPanelSkeleton />;
    $[3] = state.selectedItem;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1 || $[6] !== tabPanelProps) {
    t2 = <div className={styles["topic-section"]} data-testid="quiz-tab-content" {...tabPanelProps} ref={ref} tabIndex={0}>{t1}</div>;
    $[5] = t1;
    $[6] = tabPanelProps;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const Tabs = props => {
  const $ = _c(24);
  let abortRequest;
  let isMutationPending;
  let relativePath;
  let restProps;
  if ($[0] !== props) {
    ({
      abortRequest,
      isMutationPending,
      relativePath,
      ...restProps
    } = props);
    $[0] = props;
    $[1] = abortRequest;
    $[2] = isMutationPending;
    $[3] = relativePath;
    $[4] = restProps;
  } else {
    abortRequest = $[1];
    isMutationPending = $[2];
    relativePath = $[3];
    restProps = $[4];
  }
  const state = useTabListState(restProps);
  const ref = useRef(null);
  const topicContainerRef = useRef(null);
  let t0;
  if ($[5] !== state) {
    t0 = dataKey => state.setSelectedKey(dataKey);
    $[5] = state;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  const setActiveTab = t0;
  let t1;
  if ($[7] !== abortRequest || $[8] !== setActiveTab) {
    t1 = {
      abortRequest,
      onConfirm: setActiveTab
    };
    $[7] = abortRequest;
    $[8] = setActiveTab;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  useConfirmNavigation(isMutationPending, relativePath, t1);
  const {
    tabListProps
  } = useTabList(props, state, ref);
  let t2;
  if ($[10] !== state.collection) {
    t2 = [...state.collection];
    $[10] = state.collection;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== state || $[13] !== t2) {
    t3 = t2.map(item => <Tab item={item} key={item.key} state={state} topicContainerRef={topicContainerRef} />);
    $[12] = state;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== t3 || $[16] !== tabListProps) {
    t4 = <div className={styles["quiz-tabs-list"]} data-testid="quiz-tabs-list" {...tabListProps} ref={ref}>{t3}</div>;
    $[15] = t3;
    $[16] = tabListProps;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const t5 = state.selectedItem?.key;
  let t6;
  if ($[18] !== state || $[19] !== t5) {
    t6 = <div className={styles["topic-section-container"]} ref={topicContainerRef}><TabPanel key={t5} state={state} /></div>;
    $[18] = state;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t4 || $[22] !== t6) {
    t7 = <>{t4}{t6}</>;
    $[21] = t4;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
};
export const QuizPageContentTabs = t0 => {
  const $ = _c(45);
  const {
    cmsContent,
    initialSelfAssessmentData,
    relativePath
  } = t0;
  const assessmentQuestionsMutation = useAssessmentQuestionsMutation();
  let t1;
  if ($[0] !== cmsContent || $[1] !== initialSelfAssessmentData) {
    t1 = () => getInitialSelfAssessmentFormData(initialSelfAssessmentData, cmsContent);
    $[0] = cmsContent;
    $[1] = initialSelfAssessmentData;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [selfAssessmentFormData, setSelfAssessmentFormData] = useState(t1);
  let t2;
  if ($[3] !== assessmentQuestionsMutation) {
    t2 = async formData => await assessmentQuestionsMutation.mutate(formData);
    $[3] = assessmentQuestionsMutation;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleAssessmentQuestionsSubmit = t2;
  let t3;
  if ($[5] !== assessmentQuestionsMutation || $[6] !== cmsContent || $[7] !== initialSelfAssessmentData) {
    t3 = () => {
      assessmentQuestionsMutation.cancel();
      setSelfAssessmentFormData(getInitialSelfAssessmentFormData(initialSelfAssessmentData, cmsContent));
    };
    $[5] = assessmentQuestionsMutation;
    $[6] = cmsContent;
    $[7] = initialSelfAssessmentData;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const cancelMutation = t3;
  let t4;
  if ($[9] !== handleAssessmentQuestionsSubmit) {
    t4 = {
      mutationFunction: handleAssessmentQuestionsSubmit,
      scrollToTop: false,
      showSuccessToast: false
    };
    $[9] = handleAssessmentQuestionsSubmit;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const {
    isMutationPending,
    mutate
  } = useMutationHandler(t4);
  const {
    setIsNonBlockingLoaderVisible
  } = useGlobalLoaderContext();
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = Object.values(AssessmentQuestionsTopic);
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== cmsContent || $[13] !== selfAssessmentFormData?.assessmentTopics) {
    t6 = t5.map(topic => ({
      assessmentScore: selfAssessmentFormData?.assessmentTopics?.find(item => item?.topic === topic)?.topicScore ?? 0,
      currentTopicData: selfAssessmentFormData?.assessmentTopics?.find(topicItem => topicItem.topic === topic),
      key: topic,
      tabDescription: cmsContent?.[topic]?.TopicDescription,
      tabTitle: cmsContent?.[topic]?.Title,
      topic
    }));
    $[12] = cmsContent;
    $[13] = selfAssessmentFormData?.assessmentTopics;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const tabsData = t6;
  let t7;
  let t8;
  if ($[15] !== isMutationPending || $[16] !== setIsNonBlockingLoaderVisible) {
    t7 = () => {
      setIsNonBlockingLoaderVisible(isMutationPending);
    };
    t8 = [isMutationPending, setIsNonBlockingLoaderVisible];
    $[15] = isMutationPending;
    $[16] = setIsNonBlockingLoaderVisible;
    $[17] = t7;
    $[18] = t8;
  } else {
    t7 = $[17];
    t8 = $[18];
  }
  useEffect(t7, t8);
  let t9;
  if ($[19] !== cmsContent.backToDashboard) {
    t9 = <BackToHomepageButton label={cmsContent.backToDashboard} />;
    $[19] = cmsContent.backToDashboard;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== cmsContent.title) {
    t10 = <h1 data-testid="quiz-page-heading">{cmsContent.title}</h1>;
    $[21] = cmsContent.title;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== cmsContent.description) {
    t11 = <p className={styles["quiz-page__text"]}>{cmsContent.description}</p>;
    $[23] = cmsContent.description;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11) {
    t12 = <div className={styles["quiz-page__description-container"]}>{t10}{t11}</div>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12 || $[29] !== t9) {
    t13 = <div className={styles["quiz-page"]} data-testid="quiz-description">{t9}{t12}</div>;
    $[28] = t12;
    $[29] = t9;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== cmsContent.sliderInfo || $[32] !== mutate || $[33] !== selfAssessmentFormData) {
    t14 = itemData => <Item><div className={styles["topic-section-header"]}><h2 className={styles["topic-section-header__heading"]}>{itemData.tabTitle}</h2><p className={styles["topic-section-header__text"]}>{itemData.tabDescription}</p><Alert message={cmsContent.sliderInfo} /></div><SelfAssessmentForm mutateAssessmentQuestions={mutate} selfAssessmentFormData={selfAssessmentFormData} setSelfAssessmentFormData={setSelfAssessmentFormData} topic={itemData.topic} /></Item>;
    $[31] = cmsContent.sliderInfo;
    $[32] = mutate;
    $[33] = selfAssessmentFormData;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== cancelMutation || $[36] !== cmsContent.title || $[37] !== isMutationPending || $[38] !== relativePath || $[39] !== t14 || $[40] !== tabsData) {
    t15 = <Tabs abortRequest={cancelMutation} aria-label={cmsContent.title} isMutationPending={isMutationPending} items={tabsData} orientation="horizontal" relativePath={relativePath}>{t14}</Tabs>;
    $[35] = cancelMutation;
    $[36] = cmsContent.title;
    $[37] = isMutationPending;
    $[38] = relativePath;
    $[39] = t14;
    $[40] = tabsData;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== t13 || $[43] !== t15) {
    t16 = <>{t13}{t15}</>;
    $[42] = t13;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  return t16;
};